import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "@/lib/firebase";
import { allowDrop, getDropPayload, setDragData } from "@/dnd";
import { deleteDoc, doc, onSnapshot, runTransaction, type DocumentData, collection as fsCollection, getDocs, writeBatch } from "firebase/firestore";
import {
  addCardToBinder,
  createBinder,
  deleteBinderAndSlots,
  listenBinders,
  listenBinderSlots,
  renameBinder,
} from "@/data/binders";
import type { Binder, BinderSlot } from "@/types/binders";
import EditCollectionModal from "@/components/EditCollectionModal";

/* =========================
   BinderShelf (covers + viewer)
========================= */
export default function BinderShelf({
  uid: uidProp,
  readOnly = false,
  onTogglePublic,
}: {
  uid?: string | null;
  readOnly?: boolean;
  onTogglePublic?: (binderId: string, currentPublic: boolean) => void;
}) {
  const [uid, setUid] = useState<string | null>(uidProp ?? auth.currentUser?.uid ?? null);

  // auth (only when uid not provided)
  useEffect(() => {
    if (uidProp != null) {
      setUid(uidProp);
      return;
    }
    const unsub = onAuthStateChanged(auth, (u) => setUid(u?.uid ?? null));
    return () => unsub();
  }, [uidProp]);

  // binders
  const [binders, setBinders] = useState<Binder[]>([]);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    if (!uid) return;
    setErr(null);
    return listenBinders(
      (b) => setBinders(b),
      uid,
      (e) => setErr(e?.message || "Failed to load binders"),
    );
  }, [uid]);

  // open/close a binder viewer
  const [selectedBinderId, setSelectedBinderId] = useState<string | null>(null);
  const openBinder = useMemo(
    () => (selectedBinderId ? binders.find((b) => b.id === selectedBinderId) || null : null),
    [binders, selectedBinderId],
  );
  useEffect(() => {
    if (selectedBinderId && !openBinder) setSelectedBinderId(null);
  }, [selectedBinderId, openBinder]);

  // inline editor modal (uses EditCollectionModal)
  const [editOpen, setEditOpen] = useState(false);
  const [editCard, setEditCard] = useState<any | null>(null);
  const [editExisting, setEditExisting] = useState<{
    docId: string;
    values: {
      qty: number;
      condition: "NM" | "LP" | "MP" | "HP" | "DMG";
      foil: boolean;
      for_sale: boolean;
      for_trade: boolean;
      price: number | null;
      notes: string;
      custom_image_url: string | null;
      status?: "sale" | "trade" | "keep";
    };
  } | null>(null);

  const handleEditFromSlot = useCallback(
    (payload: {
      card: any;
      existing: {
        docId: string;
        values: {
          qty: number;
          condition: "NM" | "LP" | "MP" | "HP" | "DMG";
          foil: boolean;
          for_sale: boolean;
          for_trade: boolean;
          price: number | null;
          notes: string;
          custom_image_url: string | null;
          status?: "sale" | "trade" | "keep";
        };
      };
    }) => {
      if (readOnly) return; // block in public view
      setEditCard(payload.card);
      setEditExisting(payload.existing);
      setEditOpen(true);
    },
    [readOnly],
  );

  // create binder
  const onCreate = useCallback(async () => {
    if (!uid || readOnly) return;
    const name = prompt("Name your binder") || "Binder";
    const id = await createBinder(name, uid);
    setSelectedBinderId(id);
  }, [uid, readOnly]);

  const gridCols = "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3";

  return (
    <section className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-white font-semibold">Binders</h2>
        {!readOnly && (
          <button
            type="button"
            onClick={onCreate}
            className="px-3 py-1.5 rounded-md border border-white/10 hover:bg-white/5 text-sm text-white"
          >
            New binder
          </button>
        )}
      </div>

      {binders.length === 0 ? (
        <div className="text-sm text-slate-400">
          {readOnly ? "No binders to show." : "Create a binder to visually organize 9-up pages."}
        </div>
      ) : (
        <div className={gridCols}>
          {binders.map((b) => (
            <BinderCover
              key={b.id}
              uid={uid || ""}
              binder={{ id: b.id, name: (b as any).name || "Binder" }}
              onToggle={() => setSelectedBinderId((id) => (id === b.id ? null : b.id))}
              readOnly={readOnly}
              onTogglePublic={onTogglePublic}
            />
          ))}
        </div>
      )}

      {err && <div className="text-sm text-amber-400 mt-2">{err}</div>}

      {openBinder && uid && (
        <BinderViewer
          uid={uid}
          binder={openBinder}
          onClose={() => setSelectedBinderId(null)}
          onEditFromSlot={handleEditFromSlot}
          readOnly={readOnly}
        />
      )}

      {!readOnly && editOpen && editCard && editExisting ? (
        <EditCollectionModal
          open={editOpen}
          onClose={() => setEditOpen(false)}
          card={editCard}
          existing={editExisting}
          onUpdated={() => setEditOpen(false)}
        />
      ) : null}
    </section>
  );
}

/* =========================
   Cover (first page thumbnails + rename + drop)
========================= */
function BinderCover({
  binder,
  uid,
  onToggle,
  readOnly = false,
  onTogglePublic,
}: {
  binder: { id: string; name: string };
  uid: string;
  onToggle: () => void;
  readOnly?: boolean;
  onTogglePublic?: (binderId: string, currentPublic: boolean) => void;
}) {
  const [name, setName] = useState(binder.name);
  useEffect(() => setName(binder.name), [binder.name]);

  // listen to first page slots
  const [slots, setSlots] = useState<{ id: string; index: number; userCardId?: string | null }[]>([]);
  useEffect(() => {
    if (!uid) return;
    return listenBinderSlots(
      binder.id,
      (all) => {
        const page = all
          .filter((s) => s && typeof s.index === "number")
          .sort((a, b) => a.index - b.index)
          .slice(0, 9)
          .map((s) => ({ id: s.id, index: s.index, userCardId: (s as any).userCardId }));
        setSlots(page);
      },
      uid,
      (e) => console.warn("[BinderCover] slots error:", e?.message || e),
    );
  }, [binder.id, uid]);

  // listen for card images
  const [images, setImages] = useState<Record<string, { image: string; name: string }>>({});
  const cardUnsubsRef = useRef<Map<string, () => void>>(new Map());

  useEffect(() => {
    if (!uid) return;

    const current = new Set<string>();
    for (const s of slots) if (s.userCardId) current.add(String(s.userCardId));

    // Unsub stale
    for (const [id, off] of cardUnsubsRef.current) {
      if (!current.has(id)) {
        try { off(); } catch {}
        cardUnsubsRef.current.delete(id);
      }
    }

    // Sub new
    for (const id of current) {
      if (cardUnsubsRef.current.has(id)) continue;
      const ref = doc(db, "users", uid, "cards", id);
      const off = onSnapshot(
        ref,
        (d) => {
          const c = d.data() as DocumentData | undefined;
          setImages((prev) => ({
            ...prev,
            [id]: {
              image: (c?.custom_image_url as string) || (c?.image as string) || "/images/cardback.png",
              name: (c?.name as string) || "",
            },
          }));
        },
        () => setImages((prev) => ({ ...prev, [id]: { image: "/images/cardback.png", name: "" } })),
      );
      cardUnsubsRef.current.set(id, off);
    }

    return () => {
      for (const [, off] of cardUnsubsRef.current) {
        try { off(); } catch {}
      }
      cardUnsubsRef.current.clear();
    };
  }, [slots, uid]);

  const saveName = useCallback(() => {
    if (!uid || readOnly) return;
    const n = name.trim();
    if (n && n !== binder.name) void renameBinder(binder.id, n, uid);
  }, [binder.id, binder.name, name, uid, readOnly]);

  return (
    <div
      className="rounded-lg border border-slate-700 bg-gray-950 p-3 hover:border-slate-500 cursor-pointer"
      onClick={onToggle}
      onDragOver={readOnly ? undefined : allowDrop}
      onDrop={async (e) => {
        if (readOnly) return;
        e.preventDefault();
        e.stopPropagation();
        if (!uid) return;
        const data = getDropPayload(e);
        if (!data?.userCardId) return;

        await addCardToBinder(binder.id, data.userCardId, uid);

        if (data.sourceBinderId && data.sourceSlotId && data.sourceBinderId !== binder.id) {
          await deleteDoc(doc(db, "users", uid, "binders", data.sourceBinderId, "slots", data.sourceSlotId));
        }
      }}
      title={readOnly ? undefined : "Drop a card to add to this binder"}
    >
      {/* thumbnails */}
      <div className="grid grid-cols-3 gap-1 mb-2">
        {Array.from({ length: 9 }).map((_, i) => {
          const slot = slots[i];
          const userCardId = slot?.userCardId ? String(slot.userCardId) : "";
          const img = userCardId ? images[userCardId]?.image : null;
          const alt = userCardId ? images[userCardId]?.name || "" : "";
          return (
            <div key={i} className="aspect-[3/4] rounded overflow-hidden bg-black/50 border border-black/40">
              {img ? <img src={img} alt={alt} className="w-full h-full object-cover" loading="lazy" /> : <div />}
            </div>
          );
        })}
      </div>

      {readOnly ? (
        <div className="w-full rounded px-2 py-1 text-white text-sm text-center">{name}</div>
      ) : (
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={saveName}
          onKeyDown={(e) => e.key === "Enter" && (e.currentTarget as HTMLInputElement).blur()}
          className="w-full bg-transparent border border-transparent focus:border-slate-600 rounded px-2 py-1 text-white text-sm text-center"
        />
      )}

      {/* NEW: public/private toggle button */}
      {onTogglePublic && (
        <button
          type="button"
          className={`absolute top-7 right-1 rounded px-1 text-[10px] leading-4 ${
            binder.public
              ? "bg-emerald-600/80 hover:bg-emerald-600 text-white"
              : "bg-slate-600/70 hover:bg-slate-600 text-white/90"
          }`}
          title={binder.public ? "Set Private" : "Set Public"}
          onClick={(e) => {
            e.stopPropagation();
            onTogglePublic(binder.id, !!binder.public);
          }}
        >
          {binder.public ? "Public" : "Private"}
        </button>
      )}
    </div>
  );
}

/* =========================
   Viewer (pages, swapping, drops)
========================= */
function BinderViewer({
  uid,
  binder,
  onClose,
  onEditFromSlot,
  readOnly = false,
}: {
  uid: string;
  binder: Binder;
  onClose: () => void;
  onEditFromSlot: (payload: {
    card: any;
    existing: {
      docId: string;
      values: {
        qty: number;
        condition: "NM" | "LP" | "MP" | "HP" | "DMG";
        foil: boolean;
        for_sale: boolean;
        for_trade: boolean;
        price: number | null;
        notes: string;
        custom_image_url: string | null;
        status?: "sale" | "trade" | "keep";
      };
    };
  }) => void;
  readOnly?: boolean;
}) {
  const [slots, setSlots] = useState<BinderSlot[]>([]);
  useEffect(() => {
    return listenBinderSlots(
      binder.id,
      (s) => setSlots(s),
      uid,
      (e) => console.error("[BinderViewer] slots error:", e),
    );
  }, [binder.id, uid]);

  const pageCount = Math.max(1, Math.ceil((slots.length || 0) / 9));
  const [page, setPage] = useState(0);
  useEffect(() => setPage((p) => Math.min(p, pageCount - 1)), [pageCount]);

  const slotsOnPage = useCallback(
    (p: number) => {
      const start = p * 9;
      return slots
        .slice()
        .sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
        .slice(start, start + 9);
    },
    [slots],
  );

  // swipe gestures
  const touchStartX = useRef<number | null>(null);
  const SWIPE_THRESHOLD = 40;
  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0]?.clientX ?? null;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current == null) return;
    const dx = (e.changedTouches[0]?.clientX ?? touchStartX.current) - touchStartX.current;
    if (Math.abs(dx) > SWIPE_THRESHOLD) {
      if (dx < 0) setPage((p) => Math.min(pageCount - 1, p + 1));
      else setPage((p) => Math.max(0, p - 1));
    }
    touchStartX.current = null;
  };

  // add anywhere on binder area
  const onDropToBinder = useCallback(
    async (e: React.DragEvent) => {
      if (readOnly) return;
      e.preventDefault();
      e.stopPropagation();
      const data = getDropPayload(e);
      if (!data?.userCardId) return;
      await addCardToBinder(binder.id, data.userCardId, uid);
      if (data?.sourceBinderId && data?.sourceSlotId && data.sourceBinderId !== binder.id) {
        await deleteDoc(doc(db, "users", uid, "binders", data.sourceBinderId, "slots", data.sourceSlotId));
      }
    },
    [binder.id, uid, readOnly],
  );

  const swapSlots = useCallback(
    async (aSlotId: string, bSlotId: string) => {
      if (readOnly) return;
      const aRef = doc(db, "users", uid, "binders", binder.id, "slots", aSlotId);
      const bRef = doc(db, "users", uid, "binders", binder.id, "slots", bSlotId);
      await runTransaction(db, async (tx) => {
        const aSnap = await tx.get(aRef);
        const bSnap = await tx.get(bRef);
        const a = aSnap.data() as any;
        const b = bSnap.data() as any;
        const aIdx = typeof a?.index === "number" ? a.index : 0;
        const bIdx = typeof b?.index === "number" ? b.index : 0;
        tx.update(aRef, { index: bIdx });
        tx.update(bRef, { index: aIdx });
      });
    },
    [binder.id, uid, readOnly],
  );

  return (
    <div
      className="mt-3 rounded-xl border border-slate-700 bg-gray-950 shadow-lg overflow-hidden"
      onClick={(e) => e.stopPropagation()}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-800 bg-gray-900/60">
        <div
          className="font-semibold text-white cursor-pointer select-none"
          title="Click to close"
          onClick={onClose}
        >
          {String((binder as any).name || "Binder")}
        </div>
        <div className="flex items-center gap-2">
          <button
            className="px-2 py-1 rounded border border-white/10 hover:bg-white/5 text-sm"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            ‹ Prev
          </button>
          <div className="text-xs text-slate-400">
            Page {page + 1} / {pageCount}
          </div>
          <button
            className="px-2 py-1 rounded border border-white/10 hover:bg-white/5 text-sm"
            onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
          >
            Next ›
          </button>
          <button
            className="ml-2 px-2 py-1 rounded border border-white/10 hover:bg-white/5 text-sm"
            onClick={onClose}
            title="Close binder"
          >
            Close
          </button>
          {!readOnly && (
            <button
              className="ml-2 px-2 py-1 rounded border border-red-700/50 hover:bg-red-900/30 text-sm text-red-300"
              onClick={async () => {
                if (
                  !confirm(
                    `Delete binder "${String((binder as any).name || "Binder")}"? Cards will return to your collection.`,
                  )
                )
                  return;
                await deleteBinderAndSlots(binder.id, uid);
                onClose();
              }}
              title="Delete binder"
            >
              Delete
            </button>
          )}
        </div>
      </div>

      <div
        className="p-3"
        onDragOver={readOnly ? undefined : allowDrop}
        onDrop={readOnly ? undefined : onDropToBinder}
        title={readOnly ? undefined : "Drop a card here to add to this binder"}
      >
        <div className="grid grid-cols-3 gap-2">
          {new Array(9).fill(0).map((_, i) => {
            const slot = slotsOnPage(page)[i];
            const targetSlotId = slot?.id || null;

            return (
              <div
                key={i}
                className="aspect-[3/4] rounded border border-black/40 bg-black/50 overflow-hidden"
                onDragOver={readOnly ? undefined : allowDrop}
                onDrop={async (e) => {
                  if (readOnly) return;
                  e.preventDefault();
                  e.stopPropagation();
                  const data = getDropPayload(e);
                  if (!data?.userCardId) return;

                  if (data?.sourceBinderId && data?.sourceSlotId && data.sourceBinderId !== binder.id) {
                    await addCardToBinder(binder.id, data.userCardId, uid);
                    await deleteDoc(doc(db, "users", uid, "binders", data.sourceBinderId, "slots", data.sourceSlotId));
                    return;
                  }

                  if (data?.sourceBinderId === binder.id && data?.sourceSlotId && targetSlotId && data.sourceSlotId !== targetSlotId) {
                    await swapSlots(data.sourceSlotId, targetSlotId);
                    return;
                  }

                  if (!data?.sourceBinderId && !targetSlotId) {
                    await addCardToBinder(binder.id, data.userCardId, uid);
                  }
                }}
              >
                {slot ? (
                  <BinderSlotCard
                    uid={uid}
                    binderId={binder.id}
                    slotId={slot.id}
                    userCardId={
                      (
                        (slot as any).userCardId ??
                        (slot as any).cardId ??
                        (slot as any).user_card_id ??
                        ""
                      ) as string
                    }
                    onEditFromSlot={onEditFromSlot}
                    readOnly={readOnly}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-xs text-slate-500">Empty</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* =========================
   Slot card (image, actions, drag)
========================= */
function BinderSlotCard({
  uid,
  binderId,
  slotId,
  userCardId,
  onEditFromSlot,
  readOnly = false,
}: {
  uid: string;
  binderId: string;
  slotId: string;
  userCardId: string;
  onEditFromSlot: (payload: {
    card: any;
    existing: {
      docId: string;
      values: {
        qty: number;
        condition: "NM" | "LP" | "MP" | "HP" | "DMG";
        foil: boolean;
        for_sale: boolean;
        for_trade: boolean;
        price: number | null;
        notes: string;
        custom_image_url: string | null;
        status?: "sale" | "trade" | "keep";
      };
    };
  }) => void;
  readOnly?: boolean;
}) {
  const [img, setImg] = useState<string | null>(null);
  const [name, setName] = useState<string>("");
  const [setCode, setSetCode] = useState<string | null>(null);
  const [number, setNumber] = useState<string | null>(null);
  const [forSale, setForSale] = useState<boolean>(false);
  const [forTrade, setForTrade] = useState<boolean>(false);
  const [price, setPrice] = useState<number | null>(null);
  const [foil, setFoil] = useState<boolean>(false);
  const [qty, setQty] = useState<number>(1);
  const [condition, setCondition] = useState<"NM" | "LP" | "MP" | "HP" | "DMG">("NM");
  const [notes, setNotes] = useState<string>("");
  const [customUrl, setCustomUrl] = useState<string | null>(null);
  const [status, setStatus] = useState<"sale" | "trade" | "keep">("keep");
  const [scryfallId, setScryfallId] = useState<string | undefined>(undefined);

  // live card doc
  useEffect(() => {
    if (!uid || !userCardId) return;
    const ref = doc(db, "users", uid, "cards", userCardId);
    const unsub = onSnapshot(
      ref,
      (d) => {
        const c = d.data() as DocumentData | undefined;
        setImg((c?.custom_image_url as string) || (c?.image as string) || "/images/cardback.png");
        setName((c?.name as string) || "");
        setSetCode((c?.set as string) ?? null);
        setNumber((c?.number as string) ?? null);
        setForSale(Boolean(c?.for_sale));
        setForTrade(Boolean(c?.for_trade));
        setPrice(typeof c?.price === "number" ? (c.price as number) : null);
        setFoil(Boolean(c?.foil));
        setQty((c?.qty as number) ?? 1);
        setCondition(((c?.condition as string) ?? "NM") as any);
        setNotes((c?.notes as string) ?? "");
        setCustomUrl((c?.custom_image_url as string) ?? null);
        setStatus(((c?.status as string) ?? (c?.for_sale ? "sale" : c?.for_trade ? "trade" : "keep")) as any);
        setScryfallId((c?.scryfall_id as string) || undefined);
      },
      () => setImg("/images/cardback.png"),
    );
    return () => {
      try { unsub(); } catch {}
    };
  }, [uid, userCardId]);

  const flags = `${forSale ? "S" : ""}${forTrade ? "T" : ""}`;
  const priceText = typeof price === "number" && Number.isFinite(price) ? `$${price.toFixed(2)}` : "";

  const removeFromBinder = useCallback(async () => {
    if (readOnly) return;
    if (!confirm("Remove this card from this binder page?")) return;
    try {
      await deleteDoc(doc(db, "users", uid, "binders", binderId, "slots", slotId));
    } catch (e) {
      console.warn("[Binder] removeFromBinder failed:", e);
    }
  }, [binderId, slotId, uid, readOnly]);

  const deleteCardEverywhere = useCallback(async () => {
    if (readOnly) return;
    if (!confirm("Delete this card from your collection? This will also remove it from this binder.")) return;
    try {
      await deleteDoc(doc(db, "users", uid, "cards", userCardId));
      await deleteDoc(doc(db, "users", uid, "binders", binderId, "slots", slotId));
    } catch (e) {
      console.warn("[Binder] delete card failed:", e);
    }
  }, [binderId, slotId, uid, userCardId, readOnly]);

  const openEditor = useCallback(() => {
    if (readOnly) return;
    onEditFromSlot({
      card: {
        id: scryfallId || undefined,
        name,
        set: setCode ?? null,
        collector_number: number ?? null,
        image_uris: img ? { large: img } : undefined,
      },
      existing: {
        docId: userCardId,
        values: {
          qty,
          condition,
          foil,
          for_sale: forSale,
          for_trade: forTrade,
          price: price ?? null,
          notes,
          custom_image_url: customUrl,
          status,
        },
      },
    });
  }, [
    readOnly,
    scryfallId,
    name,
    setCode,
    number,
    img,
    userCardId,
    qty,
    condition,
    foil,
    forSale,
    forTrade,
    price,
    notes,
    customUrl,
    status,
    onEditFromSlot,
  ]);

  return (
    <div className="relative w-full h-full group">
      <button
        className="w-full h-full"
        onClick={openEditor}
        title={name ? (readOnly ? name : `Edit ${name}`) : "Card"}
        // disable drag in read-only
        draggable={!readOnly}
        onDragStart={(e) => {
          if (readOnly) return;
          setDragData(e, { userCardId, sourceBinderId: binderId, sourceSlotId: slotId });
        }}
      >
        <img
          src={img || "/images/cardback.png"}
          alt={name}
          className="w-full h-full object-cover"
          draggable={!readOnly}
          onDragStart={(e) => {
            if (readOnly) return;
            setDragData(e, { userCardId, sourceBinderId: binderId, sourceSlotId: slotId });
          }}
        />
      </button>

      {/* hover actions (hidden in read-only) */}
      {!readOnly && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 p-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="flex items-center justify-between gap-2 bg-gradient-to-t from-black/70 via-black/20 to-transparent rounded">
            <div className="flex items-center gap-1 pl-1">
              {/* edit */}
              <button
                type="button"
                className="pointer-events-auto p-1.5 rounded-md bg-white/10 hover:bg-white/20"
                aria-label="Edit"
                title="Edit"
                onClick={(e) => {
                  e.stopPropagation();
                  openEditor();
                }}
              >
                {/* icon */}
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-4 w-4 text-white">
                  <path
                    fill="currentColor"
                    d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1.003 1.003 0 0 0 0-1.42l-2.34-2.34a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z"
                  />
                </svg>
              </button>
              {/* remove */}
              <button
                type="button"
                className="pointer-events-auto p-1.5 rounded-md bg-white/10 hover:bg白/20"
                aria-label="Remove from binder"
                title="Remove from binder"
                onClick={(e) => {
                  e.stopPropagation();
                  void removeFromBinder();
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M19 13H5v-2h14v2z" />
                </svg>
              </button>
              {/* delete */}
              <button
                type="button"
                className="pointer-events-auto p-1.5 rounded-md bg-white/10 hover:bg-red-500/20"
                aria-label="Delete card"
                title="Delete card"
                onClick={(e) => {
                  e.stopPropagation();
                  void deleteCardEverywhere();
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M6 7h12v2H6V7zm2 3h8l-.8 9.6c-.1 1.1-1 1.4-2 1.4H10.8c-1 0-1.9-.3-2-1.4L8 10zm3-5h2a1 1 0 0 1 1 1v1H10V6a1 1 0 0 1 1-1z"
                  />
                </svg>
              </button>
            </div>
            {(forSale || forTrade || priceText) && (
              <div className="pointer-events-none absolute bottom-1 right-1 rounded-md bg-black/70 px-1.5 py-0.5 text-[11px] text-white shadow">
                <span className="mr-1 opacity-90">{flags}</span>
                <span className="font-medium">{priceText}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* caption */}
      <div className="absolute top-1 left-1 right-1 text-[10px] text-white/80 drop-shadow-sm">
        <div className="truncate">{name}</div>
        <div className="opacity-80">
          {(setCode || "").toUpperCase()} {number ? `• #${number}` : ""}
        </div>
      </div>
    </div>
  );
}

// Note: previewSlots was removed — BinderCover / BinderViewer derive previews from live slot state.
async function deleteBinder(uid: string, binderId: string) {
   // remove private slots + binder doc
   const slotsCol = fsCollection(db, "users", uid, "binders", binderId, "slots");
   const snap = await getDocs(slotsCol);
   const batch = writeBatch(db);
   snap.forEach((d) => batch.delete(doc(db, "users", uid, "binders", binderId, "slots", d.id)));
   batch.delete(doc(db, "users", uid, "binders", binderId));
   await batch.commit();
 
   // Cleanup public mirrors so MyCollection won't keep cards hidden
   try {
     const pubAssignCol = fsCollection(db, "users", uid, "public_binder_assignments");
     const pubSnap = await getDocs(pubAssignCol);
     const b2 = writeBatch(db);
     pubSnap.forEach((d) => {
       const v = d.data() as any;
       if (v?.binderId === binderId || d.id.startsWith(`${binderId}_`)) {
         b2.delete(d.ref);
       }
     });
     b2.delete(doc(db, "users", uid, "public_binders", binderId));
     await b2.commit();
   } catch (e) {
     console.warn("[BinderShelf] public mirror cleanup failed:", e);
   }
 }

